import type { NetworkMerchant } from "@unbox/api";
import { Globe01 } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import { MerchantLogo } from "@/components/application/merchant-logo/merchant-logo";
import { Badge } from "@/components/base/badges/badges";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useMerchant } from "@/providers/MerchantProvider";

export const NETWORK_COLUMN_TOOLTIP = "Brands in your workspace this creator already works with";

const MAX_VISIBLE_LOGOS = 2;

const useCurrentMerchantLast = (network: NetworkMerchant[]) => {
  const merchantId = useMerchant();
  return useMemo(
    () => [...network.filter((m) => m.id !== merchantId), ...network.filter((m) => m.id === merchantId)],
    [network, merchantId],
  );
};

const NetworkMerchantItem = ({ merchant }: { merchant: NetworkMerchant }) => {
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoFailed(false);
  }, [merchant.logoUrl]);

  if (!merchant.logoUrl || logoFailed) {
    return (
      <Badge type="modern" size="sm" className="w-fit">
        {merchant.name}
      </Badge>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <img
        src={merchant.logoUrl}
        alt={`${merchant.name} logo`}
        className="size-6 shrink-0 rounded-md object-cover"
        onError={() => setLogoFailed(true)}
      />
      <span className="text-sm font-medium text-secondary">{merchant.name}</span>
    </span>
  );
};

export const NetworkCellContent = ({ network: unordered }: { network: NetworkMerchant[] }) => {
  const network = useCurrentMerchantLast(unordered);
  if (network.length === 0) return <span className="text-xs font-medium text-primary">-</span>;

  const visible = network.slice(0, MAX_VISIBLE_LOGOS);

  return (
    <Tooltip
      title="Network"
      description={
        <span className="flex flex-col gap-2 pt-1">
          {network.map((merchant) => (
            <span key={merchant.id} className="flex items-center gap-2">
              <MerchantLogo name={merchant.name} logoUrl={merchant.logoUrl} size="xs" className="rounded-md" />
              <span className="text-xs font-medium text-white">{merchant.name}</span>
            </span>
          ))}
        </span>
      }
    >
      <TooltipTrigger aria-label={`Network: ${network.map((m) => m.name).join(", ")}`}>
        <span className="flex items-center gap-0.5">
          <Badge type="modern" size="sm" className="mr-0.5">
            {network.length}
          </Badge>
          {visible.map((merchant) => (
            <MerchantLogo
              key={merchant.id}
              name={merchant.name}
              logoUrl={merchant.logoUrl}
              size="xs"
              className="rounded-md"
            />
          ))}
          {network.length > MAX_VISIBLE_LOGOS && <span className="text-xs font-medium text-tertiary">…</span>}
        </span>
      </TooltipTrigger>
    </Tooltip>
  );
};

export const NetworkSection = ({
  network: unordered,
  className,
}: {
  network: NetworkMerchant[];
  className?: string;
}) => {
  const network = useCurrentMerchantLast(unordered);
  return (
    <section className={className} aria-label="Network">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Globe01 className="size-4 text-tertiary" />
            <h3 className="text-sm font-medium text-secondary">Network</h3>
          </div>
          <div className="h-px bg-border-primary" />
        </div>
        {network.length > 0 ? (
          <div className="grid grid-cols-2 gap-x-3 gap-y-2">
            {network.map((merchant) => (
              <NetworkMerchantItem key={merchant.id} merchant={merchant} />
            ))}
          </div>
        ) : (
          <div className="text-sm italic text-tertiary">No network yet</div>
        )}
      </div>
    </section>
  );
};
